import { differenceInMonths, differenceInYears } from "date-fns";

import { Account } from "../../contract.interface";

import { AccountType } from "@/modules/Accounts/types";
import { BusinessUnitPaymentMethod } from "@/modules/Settings/pages/BusinessUnits/types/types";

interface ValidationResult {
  valid: boolean;
  reason?: string;
}

const translatedAddressFields: Record<string, string> = {
  zipCode: "CEP",
  city: "Cidade",
  state: "Estado",
  street: "Logradouro",
  number: "Número",
  neighborhood: "Bairro"
};

export function validateAccountCompleteness(
  account: Partial<Account>,
  paymentMethod?: BusinessUnitPaymentMethod
): ValidationResult {
  try {
    if (!account) throw new Error("Registro de cooperado inválido");

    if (
      paymentMethod === BusinessUnitPaymentMethod.DEBITO_AUTOMATICO &&
      !account.checkingAccounts.length
    ) {
      throw new Error(
        "Você precisa adicionar uma conta para continuar esse contrato."
      );
    }

    if (!account.contacts.length) {
      // TODO: a rota está apenas buscando email
      // Quando a rota for alterada, mudar a mensagem
      throw new Error(
        "Você precisa adicionar um e-mail para continuar esse contrato."
      );
    }

    if (!account.addresses.length) {
      throw new Error(
        "Você precisa adicionar um endereço para continuar esse contrato."
      );
    }

    const requiredAddressFields = [
      "zipCode",
      "city",
      "state",
      "street",
      "number",
      "neighborhood"
    ];

    for (const address of account.addresses) {
      for (const requiredAddressField of requiredAddressFields) {
        if (!address[requiredAddressField]) {
          throw new Error(
            `O endereço está incompleto: Campo ${translatedAddressFields[requiredAddressField]} ausente.`
          );
        }
      }
    }

    const now = new Date();

    if (account.accountType === AccountType.PHYSICAL) {
      if (!account.birthDate) {
        throw new Error(
          "Você precisa adicionar uma data de nascimento para continuar esse contrato."
        );
      }

      const age = differenceInYears(now, new Date(account.birthDate));

      if (age < 18) {
        throw new Error(
          "A data de nascimento indica que o cooperado tem menos de 18 anos."
        );
      }
    }

    const wasUpdatedMoreThan12MonthsAgo = (date?: Date | string | null) =>
      !!date && differenceInMonths(now, new Date(date)) >= 12;

    const outdatedAccount = wasUpdatedMoreThan12MonthsAgo(account.updatedAt);
    const outdatedAddress = account.addresses.some(address =>
      wasUpdatedMoreThan12MonthsAgo(address.updatedAt)
    );
    const outdatedContact = account.contacts.some(contact =>
      wasUpdatedMoreThan12MonthsAgo(contact.updatedAt)
    );

    const outdated = outdatedAccount || outdatedAddress || outdatedContact;

    return {
      valid: true,
      reason:
        outdated &&
        "Existem informações nesta conta que não foram atualizadas nos últimos 12 meses. É recomendado que verifique as informações de cadastro."
    };
  } catch (error) {
    return {
      valid: false,
      reason: (error as Error).message
    };
  }
}
